import { catalogByPart } from "../apis/apiCatalogues";

/**
 * Fuente unica de verdad para las acciones que se proponen en la conclusion de
 * la investigacion y que despues se ejecutan en el flujo de atencion
 * (Resolucion > Medidas / Medida disciplinaria).
 *
 * Las compensaciones NO viven aqui: son un beneficio a la victima, no una
 * medida correctiva, y siguen con su catalogo propio (benefitOptions).
 */

// Tipos de accion homologados. Corresponden al primer nivel del catalogo
// unificado, por lo que las llaves deben existir tal cual en RS-300.
export const ACTION_TYPES = {
  MEASURE: "measure",
  SANCTION: "sanction",
};

// Catalogo unificado. Primer nivel = tipo (measure | sanction), de ahi baja el
// detalle de cada rama.
export const UNIFIED_ACTIONS_CATALOGUE = "RS-300";

// Catalogos previos a la homologacion. Se siguen consultando como respaldo para
// los tenants que aun no migran a RS-300. El orden importa: gana el primero que
// devuelva opciones.
export const LEGACY_ACTION_CATALOGUES = {
  [ACTION_TYPES.MEASURE]: ["RS-203104", "IV-102103"],
  [ACTION_TYPES.SANCTION]: ["RS-102103"],
};

// Separador con el que se arma la etiqueta legible de una ruta jerarquica.
export const ACTION_LABEL_SEPARATOR = " > ";

const ROOT_PATH = "/";

export const getLang = () => localStorage.getItem("lang") || "es";

const hasOptions = (resp) =>
  !resp?.error && Array.isArray(resp?.data) && resp.data.length > 0;

/**
 * Consulta un nivel del catalogo. Devuelve [] ante cualquier error para que el
 * llamador no tenga que distinguir "fallo" de "sin hijos".
 */
export const fetchCatalogueLevel = async ({
  catalogue,
  path = ROOT_PATH,
  isOwn = false,
}) => {
  if (!catalogue) return [];
  try {
    const resp = await catalogByPart({
      is_own: isOwn,
      catalogue,
      path: path || ROOT_PATH,
    });
    return hasOptions(resp) ? resp.data : [];
  } catch {
    return [];
  }
};

/**
 * Una ruta es hoja cuando el catalogo ya no devuelve hijos para ella. Es lo que
 * habilita el boton de "agregar" en el selector multiple.
 */
export const isLeafPath = async ({ catalogue, path, isOwn = false }) => {
  if (!catalogue || !path) return false;
  const children = await fetchCatalogueLevel({ catalogue, path, isOwn });
  return children.length === 0;
};

/**
 * Resuelve de que catalogo debe leer un tipo de accion.
 *
 * Prioriza el catalogo unificado: si existe y tiene la rama del tipo, devuelve
 * esa rama como raiz para que el selector arranque ya dentro de "medidas" o
 * "sanciones". Si no, cae al catalogo legacy correspondiente.
 *
 * Devuelve null cuando el tenant no tiene ninguno de los dos, y en ese caso el
 * formulario usa sus opciones hardcodeadas.
 */
export const resolveActionsCatalogue = async (type) => {
  const branches = await fetchCatalogueLevel({
    catalogue: UNIFIED_ACTIONS_CATALOGUE,
  });
  const branch = branches.find((item) => item.key === type);

  if (branch) {
    return {
      catalogue: UNIFIED_ACTIONS_CATALOGUE,
      isOwn: false,
      rootPath: branch.path_locales,
      isUnified: true,
    };
  }

  for (const catalogue of LEGACY_ACTION_CATALOGUES[type] || []) {
    const options = await fetchCatalogueLevel({ catalogue });
    if (options.length > 0) {
      return {
        catalogue,
        isOwn: false,
        rootPath: ROOT_PATH,
        isUnified: false,
      };
    }
  }

  return null;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

/**
 * Normaliza un elemento del catalogo al shape que consumen los selectores.
 */
export const toActionOption = (item, lang = getLang()) => ({
  value: item.path_locales,
  label: capitalize(item.locales?.[lang] || item.label),
  locales: item.locales || {},
});

/**
 * Etiqueta legible de una ruta completa ("Nivel A > Nivel B"). Se resuelve al
 * guardar y se persiste junto a la ruta para no depender del catalogo al
 * mostrar registros historicos.
 */
export const resolveActionLabel = async ({
  catalogue,
  path,
  isOwn = false,
  rootPath = ROOT_PATH,
  lang = getLang(),
}) => {
  if (!path) return "";

  // Cada nivel aporta un par "type::key". Se arranca desde rootPath para que el
  // tipo ("Medidas" / "Sanciones") no aparezca repetido en cada etiqueta.
  const segments = path.split("::");
  const startDepth =
    rootPath && rootPath !== ROOT_PATH ? rootPath.split("::").length : 0;

  const labels = [];
  let parentPath = rootPath || ROOT_PATH;

  for (let i = startDepth; i + 1 < segments.length; i += 2) {
    const level = await fetchCatalogueLevel({
      catalogue,
      path: parentPath,
      isOwn,
    });
    const item = level.find(
      (entry) => String(entry.key) === String(segments[i + 1])
    );
    if (!item) break;
    labels.push(item.locales?.[lang] || item.label);
    parentPath = item.path_locales;
  }

  return labels.join(ACTION_LABEL_SEPARATOR) || path;
};

/**
 * Shape homologado de una accion seleccionada, tanto en la conclusion de la
 * investigacion como en el flujo de atencion.
 */
export const buildAction = ({ path, label, locales = {} }) => ({
  path,
  label,
  locales,
});

/**
 * Ruta de una accion guardada. Antes de la homologacion las sanciones del flujo
 * de atencion guardaban la ruta en `value` y las medidas la guardaban suelta
 * como string, asi que se aceptan las tres formas.
 */
export const getActionPath = (action) => {
  if (!action) return "";
  if (typeof action === "string") return action;
  return action.path || action.value || "";
};

/**
 * Etiqueta a mostrar de una accion ya guardada. Acepta tanto el shape nuevo
 * ({path,label,locales}) como los strings sueltos que dejaron las versiones
 * previas, para que los registros historicos no se vean en blanco.
 */
export const getActionLabel = (action, lang = getLang()) => {
  if (!action) return "";
  if (typeof action === "string") return action;
  return action.locales?.[lang] || action.label || getActionPath(action) || "";
};

/**
 * Shape homologado de una accion propuesta en la conclusion de la
 * investigacion. Normaliza tambien los registros guardados antes de la
 * homologacion, que traian una sola medida en `measure` + `measureCatalogPath`.
 */
/** Clasificacion elegida para la propuesta: { path, label } o null. */
const normalizeActionCaseType = (caseType) => {
  const path = typeof caseType?.path === "string" ? caseType.path : "";
  if (!path) return null;
  return {
    path,
    label: typeof caseType?.label === "string" ? caseType.label : "",
  };
};

export const normalizeProposedAction = (raw) => {
  if (!raw) return null;

  const actions = (Array.isArray(raw.actions) ? raw.actions : []).map((item) =>
    buildAction({
      path: getActionPath(item),
      label: getActionLabel(item),
      locales: item?.locales || {},
    })
  );
  const another = Array.isArray(raw.another) ? raw.another : [];

  // Registro previo: una unica medida, sin tipo ni involucrado.
  if (actions.length === 0 && another.length === 0 && raw.measure) {
    const legacy = raw.measureCatalogPath
      ? [buildAction({ path: raw.measureCatalogPath, label: raw.measure })]
      : [];
    return {
      type: raw.type || ACTION_TYPES.MEASURE,
      actions: legacy,
      another: legacy.length === 0 ? [raw.measure] : [],
      involved: raw.involved || null,
      case_type: normalizeActionCaseType(raw.case_type),
      details: raw.details || "",
    };
  }

  return {
    type: raw.type || ACTION_TYPES.MEASURE,
    actions,
    another,
    involved: raw.involved || null,
    // Por cual clasificacion del caso se propone (folder.case_classification).
    // Null en las propuestas hechas antes de que existiera la eleccion.
    case_type: normalizeActionCaseType(raw.case_type),
    details: raw.details || "",
  };
};

/**
 * Etiquetas a mostrar de una accion propuesta, ya sea del catalogo o capturada
 * a mano.
 */
export const getProposedActionLabels = (raw, lang = getLang()) => {
  const action = normalizeProposedAction(raw);
  if (!action) return [];
  return [
    ...action.actions.map((item) => getActionLabel(item, lang)),
    ...action.another.filter(Boolean),
  ];
};

/**
 * Shape homologado de una medida registrada en el flujo de atencion.
 * Normaliza tambien los registros previos, que guardaban una sola medida en
 * `measure` y el texto libre en `another_measure`.
 */
export const normalizeResolutionMeasure = (raw) => {
  if (!raw) return { type: ACTION_TYPES.MEASURE, actions: [], another: [] };

  const actions = (Array.isArray(raw.measures) ? raw.measures : []).map((item) => ({
    // Se conservan los campos extra del registro (p.ej. from_investigation).
    ...(typeof item === "object" && item ? item : {}),
    ...buildAction({
      path: getActionPath(item),
      label: typeof item === "string" ? "" : item?.label || "",
      locales: item?.locales || {},
    }),
  }));

  // El texto libre se guarda con distintas llaves segun la seccion (y como
  // string suelto en los registros previos); aqui se unifica a
  // { text, from_investigation } para tratarlo igual en todos lados.
  const another = (Array.isArray(raw.another) ? raw.another : [])
    .map((item) => {
      if (typeof item === "string") return { text: item };
      const text =
        item?.text ||
        item?.another_measure ||
        item?.another_sanction ||
        item?.another_benefit ||
        "";
      return { text, from_investigation: item?.from_investigation };
    })
    .filter((item) => item.text);

  if (actions.length === 0 && another.length === 0 && raw.measure) {
    // Registro previo a la homologacion.
    if (raw.measure === "another") {
      return {
        type: ACTION_TYPES.MEASURE,
        actions: [],
        another: raw.another_measure ? [{ text: raw.another_measure }] : [],
      };
    }
    return {
      type: ACTION_TYPES.MEASURE,
      actions: [buildAction({ path: raw.measure, label: "" })],
      another: [],
    };
  }

  return { type: raw.type || ACTION_TYPES.MEASURE, actions, another };
};

/**
 * Aplana las acciones que un investigador propuso en su conclusion, para que
 * Resolucion las presente una por una y el resolutor pueda confirmarlas,
 * descartarlas o agregar otras.
 *
 * Cada propuesta se queda con la investigacion de la que salio: la pantalla
 * muestra varias investigaciones a la vez y todas sus propuestas conviven en
 * una sola lista, asi que la marca tiene que viajar en el renglon.
 *
 * @param detail Respuesta (ya desenvuelta) de getInvestigation(alias)
 * @param investigation Snapshot de la investigacion para marcar cada propuesta
 * @param type Filtra por tipo de accion (measure | sanction)
 * @param involvedName Solo las propuestas dirigidas a esa persona (sanciones)
 */
export const getInvestigationProposals = (
  detail,
  { investigation, type, involvedName } = {}
) => {
  const measures = Array.isArray(detail?.measures) ? detail.measures : [];
  const prefix = investigation?.alias || "";

  const proposals = [];

  measures.forEach((raw, idx) => {
    const action = normalizeProposedAction(raw);
    if (!action) return;
    if (type && action.type !== type) return;
    // Las sanciones se registran por persona, asi que solo aplican las que el
    // investigador dirigio a ese involucrado.
    if (involvedName && action.involved?.name !== involvedName) return;

    action.actions.forEach((item, i) => {
      proposals.push({
        id: `${prefix}::${idx}-c${i}`,
        key: getProposalKey({
          alias: prefix,
          kind: "catalogue",
          path: getActionPath(item),
        }),
        investigation,
        kind: "catalogue",
        path: getActionPath(item),
        label: getActionLabel(item),
        locales: item.locales || {},
        details: action.details || "",
      });
    });

    action.another.filter(Boolean).forEach((text, i) => {
      proposals.push({
        id: `${prefix}::${idx}-f${i}`,
        key: getProposalKey({ alias: prefix, kind: "free", text }),
        investigation,
        kind: "free",
        text,
        details: action.details || "",
      });
    });
  });

  return proposals;
};

/**
 * Separa las propuestas en lo que se aplica y lo que se descarta, en el shape
 * que espera el API de resolucion.
 *
 * Cada renglon queda marcado con la investigacion de la que salio (la trae la
 * propia propuesta): en el registro final las confirmadas se mezclan con las
 * que el resolutor agrego por su cuenta, y sin la marca no habria como
 * distinguirlas. Por eso el texto libre tambien sale como objeto, no como
 * string.
 */
export const splitProposals = (proposals = []) => {
  const confirmed = [];
  const another = [];
  const discarded = [];

  proposals.forEach((proposal) => {
    // Pendiente: nadie ha decidido. No se guarda y sigue disponible para otro
    // registro de la misma persona.
    if (proposal.confirmed == null) return;
    const origin = proposal.investigation
      ? { from_investigation: proposal.investigation }
      : {};
    if (proposal.confirmed === false) {
      discarded.push({
        ...(proposal.kind === "free"
          ? { text: proposal.text }
          : buildAction({
              path: proposal.path,
              label: proposal.label,
              locales: proposal.locales,
            })),
        ...origin,
        reason: (proposal.reason || "").trim(),
      });
      return;
    }
    if (proposal.kind === "free") {
      another.push({ text: proposal.text, ...origin });
    } else {
      confirmed.push({
        ...buildAction({
          path: proposal.path,
          label: proposal.label,
          locales: proposal.locales,
        }),
        ...origin,
      });
    }
  });

  return { confirmed, another, discarded };
};

/**
 * Identidad de una propuesta por su contenido, no por su posicion. Es lo que
 * permite reconocer, al reabrir un registro, cual propuesta ya se habia
 * confirmado y cual se habia descartado, aunque el investigador haya
 * reordenado o agregado otras despues.
 */
export const getProposalKey = ({ alias, kind, path, text }) =>
  `${alias || ""}::${kind === "free" ? `f::${text}` : `c::${path}`}`;

/**
 * Reconstruye el estado de las propuestas a partir de un registro guardado:
 * lo que quedo aplicado vuelve marcado y lo descartado vuelve desmarcado con
 * su motivo. Lo que el resolutor agrego por su cuenta no entra aqui, porque no
 * es una propuesta del investigador.
 */
export const getSavedProposalStates = ({
  actions = [],
  another = [],
  discarded = [],
} = {}) => {
  const states = {};

  (actions || []).forEach((item) => {
    const alias = item?.from_investigation?.alias;
    if (!alias) return;
    states[
      getProposalKey({ alias, kind: "catalogue", path: getActionPath(item) })
    ] = { confirmed: true, reason: "" };
  });

  (another || []).forEach((item) => {
    const alias = item?.from_investigation?.alias;
    if (!alias) return;
    states[getProposalKey({ alias, kind: "free", text: item.text })] = {
      confirmed: true,
      reason: "",
    };
  });

  (discarded || []).forEach((item) => {
    const alias = item?.from_investigation?.alias;
    const kind = item?.text ? "free" : "catalogue";
    states[
      getProposalKey({
        alias,
        kind,
        path: getActionPath(item),
        text: item?.text,
      })
    ] = { confirmed: false, reason: item?.reason || "" };
  });

  return states;
};

/**
 * Texto libre de un registro guardado, unificado a { text, from_investigation }.
 * Cada seccion lo guarda con su propia llave, y los registros previos como
 * string suelto.
 */
export const normalizeSavedAnother = (another = []) =>
  (another || []).map((item) =>
    typeof item === "string"
      ? { text: item }
      : {
          text:
            item?.another_sanction || item?.another_benefit || item?.text || "",
          from_investigation: item?.from_investigation,
        }
  );

/**
 * Propuestas que ya se decidieron (aplicadas o descartadas) en estos registros.
 *
 * Una propuesta se resuelve una sola vez por persona: si otro registro la vuelve
 * a ofrecer, se duplica la sancion o se revierte un descarte con motivo.
 */
export const getResolvedProposalKeys = (records = []) => {
  const keys = new Set();
  (records || []).forEach((record) => {
    const states = getSavedProposalStates({
      actions: record?.sanctions,
      another: normalizeSavedAnother(record?.another),
      discarded: record?.discarded,
    });
    Object.keys(states).forEach((key) => keys.add(key));
  });
  return [...keys];
};

// Los registros previos guardan solo la llave ("dismisal") y los nuevos la ruta
// completa ("type::sanction::type::dismisal"): se comparan por el ultimo nivel.
const getActionCode = (action) => getActionPath(action).split("::").pop();

const normalizeText = (text) => (text || "").trim().toLowerCase();

/**
 * De lo que se esta capturando, lo que la persona ya tiene en otros registros.
 * Es solo un aviso: dos equipos pueden registrar lo mismo a proposito.
 */
export const findAlreadyRegistered = ({
  records = [],
  actionsKey,
  actions = [],
  another = [],
} = {}) => {
  const codes = new Set();
  const texts = new Set();
  (records || []).forEach((record) => {
    (record?.[actionsKey] || []).forEach((item) =>
      codes.add(getActionCode(item))
    );
    normalizeSavedAnother(record?.another).forEach(({ text }) =>
      texts.add(normalizeText(text))
    );
  });

  return [
    ...actions
      .filter((item) => codes.has(getActionCode(item)))
      .map((item) => getActionLabel(item)),
    ...another.filter((text) => texts.has(normalizeText(text))),
  ];
};

/** Deja juntos los registros de cada persona, en el orden en que aparecieron. */
export const groupRecordsByPerson = (records = []) => {
  const byName = new Map();
  (records || []).forEach((record) => {
    byName.set(record?.name, [...(byName.get(record?.name) || []), record]);
  });
  return [...byName.values()].flat();
};

/**
 * Separa lo que salio de una investigacion de lo que capturo el resolutor. Al
 * editar, lo primero se maneja en el bloque de propuestas y lo segundo en el
 * selector de catalogo; mezclarlos duplicaria renglones al guardar.
 */
export const splitByOrigin = (items = []) => {
  const fromInvestigation = [];
  const own = [];
  (items || []).forEach((item) => {
    if (item?.from_investigation?.alias) fromInvestigation.push(item);
    else own.push(item);
  });
  return { fromInvestigation, own };
};
